import { Geofence, ReferencePoint, SafeZone } from "@/interfaces/index.js";
import { errorHandler } from "@/lib/utils.js";
import { userService } from "@/services/api/user.js";
import L from "leaflet";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet/dist/leaflet.css";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FeatureGroup, MapContainer, useMap } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import MapTileLayers from "./MapTileLayers";
import "../../assets/locales/leafletDrawPtBr.js";
import { AuthContext } from "../../contexts/auth/AuthContext";
import MapForm from "./MapForm.js";

interface AreaDrawingMapProps {
  onPolygonsChange: (polygons: number[][][]) => void;
  onCirclesChange: (
    circles: { center: [number, number]; radius: number }[],
  ) => void;
  initialPolygons: number[][][];
  initialCircles: { center: [number, number]; radius: number }[];
  toLoad: "all" | "reference_points" | "safe_zones" | "geofences";
  editingAreaId?: number;
  drawingMode?: "polygon" | "circle" | "both";
  showCircleForm?: boolean;
}

interface GeographicAreasResponse {
  errors: boolean;
  data: {
    reference_points: ReferencePoint[];
    safe_zones: SafeZone[];
    geofences: Geofence[];
  };
}

interface DrawCreatedEvent {
  layer: L.Layer;
  layerType: string;
}

interface DrawEditedEvent {
  layers: L.LayerGroup;
}

interface DrawDeletedEvent {
  layers: L.LayerGroup;
}

interface CircleFormData {
  latitude: string;
  longitude: string;
  radius: string;
}

const calculateZoomForCircle = (radius: number, map: L.Map) => {
  const metersPerPixel = 156543.03392;
  const radiusInPixels = radius / metersPerPixel;

  const mapWidth = map.getSize().x;
  const mapHeight = map.getSize().y;

  const targetSize = Math.min(mapWidth, mapHeight) * 0.6;

  const zoom = Math.log2(targetSize / (radiusInPixels * 2));

  const adjustedZoom = Math.round(zoom);

  return Math.max(0, Math.min(20, adjustedZoom));
};

const MapController = ({
  circle,
  polygon,
}: {
  circle?: { center: [number, number]; radius: number };
  polygon?: L.LatLng[];
}) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      const timeoutId = setTimeout(() => {
        try {
          if (circle) {
            const center = L.latLng(circle.center[0], circle.center[1]);
            const zoom = calculateZoomForCircle(circle.radius, map);
            map.setView(center, zoom, {
              animate: true,
              duration: 1,
            });
          } else if (polygon) {
            const bounds = L.latLngBounds(polygon);
            map.fitBounds(bounds, { animate: true, duration: 1 });
          }
        } catch (err) {
          errorHandler(err, "Erro ao ajustar o mapa");
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [circle, polygon, map]);

  return null;
};

const AreaDrawingMap = ({
  onPolygonsChange,
  onCirclesChange,
  initialPolygons,
  initialCircles,
  toLoad,
  editingAreaId,
  drawingMode = "both",
  showCircleForm = true,
}: AreaDrawingMapProps) => {
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const existingAreasRef = useRef<L.FeatureGroup>(null);
  const authContext = useContext(AuthContext);
  const userId = authContext?.userId;
  const [geographicAreas, setGeographicAreas] =
    useState<GeographicAreasResponse | null>(null);

  useEffect(() => {
    const fetchGeographicAreas = async () => {
      try {
        const response = await userService.getGeographicAreas(
          userId as number,
          toLoad,
        );
        setGeographicAreas(response);
      } catch (err) {
        errorHandler(err, "Erro ao carregar áreas geográficas");
      }
    };

    fetchGeographicAreas();
  }, [toLoad, userId]);

  const [mapReady, setMapReady] = useState(false);
  const [currentCircle, setCurrentCircle] = useState<{
    center: [number, number];
    radius: number;
  }>();
  const [currentPolygon, setCurrentPolygon] = useState<L.LatLng[]>();

  const form = useForm<CircleFormData>({
    defaultValues: {
      latitude: "",
      longitude: "",
      radius: "",
    },
  });

  useEffect(() => {
    if (featureGroupRef.current && mapReady) {
      const group = featureGroupRef.current;
      group.clearLayers();

      try {
        initialPolygons.forEach((polygon) => {
          const latLngs = polygon.map((coord) => L.latLng(coord[0], coord[1]));
          L.polygon(latLngs).addTo(group);
          setCurrentPolygon(latLngs);
        });

        if (initialCircles.length > 0) {
          const circle = initialCircles[0];
          L.circle(L.latLng(circle.center[0], circle.center[1]), {
            radius: circle.radius,
          }).addTo(group);
          setCurrentCircle(circle);

          form.setValue("latitude", circle.center[0].toFixed(6));
          form.setValue("longitude", circle.center[1].toFixed(6));
          form.setValue("radius", circle.radius.toFixed(0));
        }
      } catch (err) {
        errorHandler(err, "Erro ao carregar áreas geográficas");
      }
    }
  }, [initialPolygons, initialCircles, mapReady]);

  const onCreated = (e: DrawCreatedEvent) => {
    const layer = e.layer;
    const type = e.layerType;

    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }

    if (type === "polygon" && layer instanceof L.Polygon) {
      const polygonCoords = (layer.getLatLngs()[0] as L.LatLng[]).map(
        (latLng) => [latLng.lat, latLng.lng] as [number, number],
      );
      onPolygonsChange([polygonCoords]);
      onCirclesChange([]);
      setCurrentCircle(undefined);
      setCurrentPolygon(layer.getLatLngs()[0] as L.LatLng[]);
    } else if (type === "circle" && layer instanceof L.Circle) {
      const center = layer.getLatLng();
      const radius = layer.getRadius();

      onCirclesChange([{ center: [center.lat, center.lng], radius }]);
      onPolygonsChange([]);
      setCurrentCircle({ center: [center.lat, center.lng], radius });
      setCurrentPolygon(undefined);

      form.setValue("latitude", center.lat.toFixed(6));
      form.setValue("longitude", center.lng.toFixed(6));
      form.setValue("radius", radius.toFixed(0));
    }
  };

  const onEdited = (e: DrawEditedEvent) => {
    const layers = e.layers;
    layers.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        const latLngs = layer.getLatLngs()[0] as L.LatLng[];
        const polygonCoords = latLngs.map(
          (latLng) => [latLng.lat, latLng.lng] as [number, number],
        );
        onPolygonsChange([polygonCoords]);
        setCurrentCircle(undefined);
        setCurrentPolygon(latLngs);
      } else if (layer instanceof L.Circle) {
        const center = layer.getLatLng();
        const radius = layer.getRadius();

        onCirclesChange([{ center: [center.lat, center.lng], radius }]);
        setCurrentCircle({ center: [center.lat, center.lng], radius });
        setCurrentPolygon(undefined);

        form.setValue("latitude", center.lat.toFixed(6));
        form.setValue("longitude", center.lng.toFixed(6));
        form.setValue("radius", radius.toFixed(0));
      }
    });
  };

  const onDeleted = (e: DrawDeletedEvent) => {
    const layers = e.layers;
    layers.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        onPolygonsChange([]);
        setCurrentPolygon(undefined);
      } else if (layer instanceof L.Circle) {
        onCirclesChange([]);
        setCurrentCircle(undefined);
      }
    });
  };

  const handleAddCircle = (data: CircleFormData) => {
    const lat = parseFloat(data.latitude);
    const lng = parseFloat(data.longitude);
    const rad = parseFloat(data.radius);

    if (featureGroupRef.current && !isNaN(lat) && !isNaN(lng) && !isNaN(rad)) {
      featureGroupRef.current.clearLayers();

      const circle = L.circle([lat, lng], { radius: rad });
      featureGroupRef.current.addLayer(circle);
      const newCircle = { center: [lat, lng] as [number, number], radius: rad };
      onCirclesChange([newCircle]);
      onPolygonsChange([]);
      setCurrentCircle(newCircle);
      setCurrentPolygon(undefined);
    }
  };

  const handleUpdateCircleRadius = (radius: number) => {
    if (currentCircle && featureGroupRef.current) {
      const { center } = currentCircle;

      const circle = L.circle([center[0], center[1]], { radius });
      featureGroupRef.current.clearLayers();
      featureGroupRef.current.addLayer(circle);

      const newCircle = { center, radius };
      onCirclesChange([newCircle]);
      setCurrentCircle(newCircle);
    }
  };

  useEffect(() => {
    if (existingAreasRef.current && mapReady && geographicAreas) {
      const group = existingAreasRef.current;
      group.clearLayers();

      const filterEditingArea = <
        T extends SafeZone | ReferencePoint | Geofence,
      >(
        areas: T[],
      ) => {
        return areas.filter((area) => area.id !== editingAreaId);
      };

      if (geographicAreas.data.reference_points?.length) {
        const filteredReferencePoints = filterEditingArea(
          geographicAreas.data.reference_points,
        );
        filteredReferencePoints.forEach((item) => {
          if (item.center_latitude && item.center_longitude && item.radius) {
            const circle = L.circle(
              [
                parseFloat(item.center_latitude),
                parseFloat(item.center_longitude),
              ],
              {
                radius: item.radius,
                color: "#5AAACB",
                fillColor: "#5AAACB",
                fillOpacity: 0.3,
              },
            ).addTo(group);
            circle.bindTooltip(item.name);
          } else if (item.polygon_coordinates) {
            const coords = item.polygon_coordinates.split(";").map((coord) => {
              const [lat, lng] = coord.split(",").map(Number);
              return L.latLng(lat, lng);
            });
            const polygon = L.polygon(coords, {
              color: "#5AAACB",
              fillColor: "#5AAACB",
              fillOpacity: 0.3,
            }).addTo(group);
            polygon.bindTooltip(item.name);
          }
        });
      }

      if (geographicAreas.data.safe_zones?.length) {
        const filteredSafeZones = filterEditingArea(
          geographicAreas.data.safe_zones,
        );
        filteredSafeZones.forEach((item) => {
          if (item.center_coordinates) {
            const [lat, lng] = item.center_coordinates.split(",").map(Number);
            const circle = L.circle([lat, lng], {
              radius: item.radius ? parseFloat(item.radius.toString()) : 0,
              color: "#74f274",
              fillColor: "#74f274",
              fillOpacity: 0.3,
            }).addTo(group);
            circle.bindTooltip(item.name);
          }
        });
      }

      if (geographicAreas.data.geofences?.length) {
        const filteredGeofences = filterEditingArea(
          geographicAreas.data.geofences,
        );
        filteredGeofences.forEach((item) => {
          const coords = item.coordinates.split(";").map((coord) => {
            const [lat, lng] = coord.split(",").map(Number);
            return L.latLng(lat, lng);
          });
          const polygon = L.polygon(coords, {
            color: "#eef274",
            fillColor: "#eef274",
            fillOpacity: 0.3,
          }).addTo(group);
          polygon.bindTooltip(item.title);
        });
      }
    }
  }, [geographicAreas, mapReady, editingAreaId]);

  const getDrawOptions = () => {
    const options = {
      polygon: drawingMode === "polygon" || drawingMode === "both",
      circle: drawingMode === "circle" || drawingMode === "both",
      rectangle: false,
      marker: false,
      circlemarker: false,
      polyline: false,
    };

    return { draw: options };
  };

  return (
    <MapContainer
      center={[-11.59293, -49.597924]}
      zoom={4}
      className={`w-full h-full z-0`}
      whenReady={() => {
        setMapReady(true);
      }}
    >
      {mapReady && (
        <MapController circle={currentCircle} polygon={currentPolygon} />
      )}
      <MapTileLayers osmLabel="Detalhado" />

      <FeatureGroup ref={existingAreasRef} />

      <FeatureGroup ref={featureGroupRef}>
        <EditControl
          position="topright"
          onCreated={onCreated}
          onEdited={onEdited}
          onDeleted={onDeleted}
          {...getDrawOptions()}
        />
      </FeatureGroup>

      {showCircleForm && (
        <MapForm
          form={form}
          onSubmit={form.handleSubmit(handleAddCircle)}
          handleUpdateCircleRadius={handleUpdateCircleRadius}
        />
      )}
    </MapContainer>
  );
};

export default AreaDrawingMap;
